import type { CycleOperatorSummary, EntryObservabilityResponse } from "./types";

export type ReviewReadinessStatus =
  | "counting"
  | "prep"
  | "ready"
  | "diagnostic"
  | "awaiting_ship"
  | "unknown";

export type WeakSliceExhaustionThresholds = {
  stallHours: number;
  minRtsAfterLastWeak: number;
  minCaptureFraction: number;
  minCaptureAtMinRt: number;
};

export type ReviewReadinessLayer = {
  actual: number | null;
  target?: number | null;
  required?: number;
  remaining: number | null;
  percent: number | null;
  met: boolean;
  stallCapTarget?: number | null;
  usesWeakIc0Slice?: boolean;
  qualityBasis?: "weak_ic0_slice" | "total_rt" | "total_rt_after_weak_exhaustion";
  weakSliceExhausted?: boolean;
  bindingActual?: number | null;
  lastWeakIc0RoundTripAt?: string | null;
  roundTripsSinceLastWeakIc0?: number | null;
  stallHoursSinceLastWeakIc0?: number | null;
  exhaustionThresholds?: WeakSliceExhaustionThresholds;
};

export type ReviewReadiness = {
  protocolLabel: string;
  status: ReviewReadinessStatus;
  statusLabelKo: string;
  bindingReady: boolean;
  diagnosticEligible: boolean;
  fillLayer: ReviewReadinessLayer;
  qualityLayer: ReviewReadinessLayer;
  regimeLayer: {
    contradictionRate24h: number | null;
    crisis: boolean;
    stable: boolean | null;
  };
  detailKo: string;
  metaKo?: string;
  metricsSource?: string;
};

const REGIME_CRISIS_CONTRADICTION_SHARE = 0.8;
const DIAGNOSTIC_MIN_ENTRY_CANDIDATES_24H = 100;
const DIAGNOSTIC_MIN_ROUND_TRIPS = 20;
const ENTER_VERDICTS_PER_HOUR_NEAR_ZERO = 0.1;
const DEFAULT_MIN_ROUND_TRIPS_PER_REVIEW = 40;
const DEFAULT_FILLS_PER_REVIEW = 50;
const ROUND_TRIP_FILL_PROXY_RATIO = 0.3;
const WEAK_SLICE_EXHAUSTION_STALL_HOURS = 12;
const WEAK_SLICE_EXHAUSTION_MIN_RTS_AFTER_LAST_WEAK = 9;
const WEAK_SLICE_EXHAUSTION_MIN_CAPTURE_FRACTION = 0.9;

function nullishNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Fill gates must be positive; 0/negative/NaN are treated as unset (awaiting ship). */
function resolvePositiveFillGate(value: unknown): number | null {
  const n = nullishNumber(value);
  return n != null && n > 0 ? n : null;
}

/** Apply quality-layer RT overrides; mirrors scripts/lib/review-readiness.mjs. */
function hydrateMetricsFromQualityLayer(
  metrics: {
    roundTripsSinceImprovement?: number | null;
    weakIc0RoundTripsSinceImprovement?: number | null;
    lastWeakIc0RoundTripAt?: string | null;
    roundTripsSinceLastWeakIc0?: number | null;
    [key: string]: unknown;
  },
  qualityLayer?: ReviewReadinessLayer | null,
) {
  const hydrated = { ...metrics };
  if (!qualityLayer) return hydrated;

  const bindingActual = nullishNumber(qualityLayer.bindingActual);
  const actual = nullishNumber(qualityLayer.actual);

  if (qualityLayer.weakSliceExhausted && bindingActual != null) {
    hydrated.roundTripsSinceImprovement = bindingActual;
    if (qualityLayer.usesWeakIc0Slice && actual != null) {
      hydrated.weakIc0RoundTripsSinceImprovement = actual;
    }
  } else if (hydrated.roundTripsSinceImprovement == null && actual != null) {
    hydrated.roundTripsSinceImprovement = actual;
    if (qualityLayer.usesWeakIc0Slice) {
      hydrated.weakIc0RoundTripsSinceImprovement = actual;
    }
  }

  if (hydrated.lastWeakIc0RoundTripAt == null) {
    hydrated.lastWeakIc0RoundTripAt = qualityLayer.lastWeakIc0RoundTripAt ?? null;
  }
  if (hydrated.roundTripsSinceLastWeakIc0 == null) {
    hydrated.roundTripsSinceLastWeakIc0 = qualityLayer.roundTripsSinceLastWeakIc0 ?? null;
  }

  return hydrated;
}

function isAwaitingImprovementShip(cycle?: CycleOperatorSummary | null) {
  if (!cycle) return false;
  return (
    cycle.improvementPhase === "awaiting-improvement-ship" ||
    (cycle.improvementPhase === "post-review" && cycle.nextReviewAtFills == null)
  );
}

function buildLayer(
  actual: number | null,
  target: number | null,
  metOverride: boolean | null = null,
) {
  const met = metOverride ?? (actual != null && target != null && actual >= target);
  const remaining = actual != null && target != null ? Math.max(0, target - actual) : null;
  const percent =
    actual != null && target != null && target > 0
      ? Math.min(100, Math.round((actual / target) * 100))
      : null;
  return { actual, target, remaining, percent, met };
}

export function formatQualityLayerLabel(qualityLayer: ReviewReadinessLayer): string | null {
  const actual = qualityLayer.actual;
  const required = qualityLayer.required;
  if (actual == null || required == null) return null;

  const exhausted =
    qualityLayer.weakSliceExhausted === true ||
    qualityLayer.qualityBasis === "total_rt_after_weak_exhaustion";
  if (exhausted) {
    const displayActual = qualityLayer.bindingActual ?? actual;
    const metSuffix = qualityLayer.met ? " ✓" : "";
    return `total RT ${displayActual}/${required}${metSuffix}`;
  }

  const weakSlice =
    qualityLayer.qualityBasis === "weak_ic0_slice" || qualityLayer.usesWeakIc0Slice === true;
  if (weakSlice) {
    return `weak·ic0 RT ${actual}/${required}`;
  }

  return `RT ${actual}/${required}`;
}

function buildExhaustionThresholds(minRt: number): WeakSliceExhaustionThresholds {
  return {
    stallHours: WEAK_SLICE_EXHAUSTION_STALL_HOURS,
    minRtsAfterLastWeak: WEAK_SLICE_EXHAUSTION_MIN_RTS_AFTER_LAST_WEAK,
    minCaptureFraction: WEAK_SLICE_EXHAUSTION_MIN_CAPTURE_FRACTION,
    minCaptureAtMinRt: Math.ceil(minRt * WEAK_SLICE_EXHAUSTION_MIN_CAPTURE_FRACTION),
  };
}

function evaluateWeakSliceExhaustion(
  metrics: {
    lastWeakIc0RoundTripAt?: string | null;
    roundTripsSinceLastWeakIc0?: number | null;
  },
  {
    weakRt,
    totalRt,
    minRt,
    nowMs = Date.now(),
  }: {
    weakRt: number | null;
    totalRt: number | null;
    minRt: number;
    nowMs?: number;
  },
) {
  const thresholds = buildExhaustionThresholds(minRt);
  const lastWeakAt = metrics.lastWeakIc0RoundTripAt ?? null;
  const rtsSinceLastWeak = nullishNumber(metrics.roundTripsSinceLastWeakIc0);
  const lastWeakMs = lastWeakAt ? Date.parse(lastWeakAt) : Number.NaN;
  const stallHours =
    Number.isFinite(lastWeakMs) && Number.isFinite(nowMs)
      ? (nowMs - lastWeakMs) / (1000 * 60 * 60)
      : null;

  const exhausted =
    weakRt != null &&
    weakRt < minRt &&
    totalRt != null &&
    totalRt >= minRt &&
    weakRt >= thresholds.minCaptureAtMinRt &&
    rtsSinceLastWeak != null &&
    rtsSinceLastWeak >= WEAK_SLICE_EXHAUSTION_MIN_RTS_AFTER_LAST_WEAK &&
    stallHours != null &&
    stallHours >= WEAK_SLICE_EXHAUSTION_STALL_HOURS;

  return {
    exhausted,
    stallHoursSinceLastWeakIc0: stallHours,
    exhaustionThresholds: thresholds,
  };
}

function resolveQualityLayer({
  metrics,
  minRt,
  nowMs = Date.now(),
}: {
  metrics: {
    roundTripsSinceImprovement?: number | null;
    weakIc0RoundTripsSinceImprovement?: number | null;
    lastWeakIc0RoundTripAt?: string | null;
    roundTripsSinceLastWeakIc0?: number | null;
  };
  minRt: number;
  nowMs?: number;
}): ReviewReadinessLayer {
  const weakRt = nullishNumber(metrics.weakIc0RoundTripsSinceImprovement);
  const totalRt = nullishNumber(metrics.roundTripsSinceImprovement);
  const qualityActual =
    metrics.weakIc0RoundTripsSinceImprovement != null && weakRt != null ? weakRt : (totalRt ?? 0);
  const exhaustion = evaluateWeakSliceExhaustion(metrics, { weakRt, totalRt, minRt, nowMs });
  const qualityBasis = exhaustion.exhausted
    ? "total_rt_after_weak_exhaustion"
    : weakRt != null
      ? "weak_ic0_slice"
      : "total_rt";

  return {
    ...buildLayer(qualityActual, minRt, exhaustion.exhausted ? true : null),
    required: minRt,
    usesWeakIc0Slice: weakRt != null,
    qualityBasis,
    weakSliceExhausted: exhaustion.exhausted,
    bindingActual: exhaustion.exhausted ? totalRt : qualityActual,
    lastWeakIc0RoundTripAt: metrics.lastWeakIc0RoundTripAt ?? null,
    roundTripsSinceLastWeakIc0: metrics.roundTripsSinceLastWeakIc0 ?? null,
    stallHoursSinceLastWeakIc0: exhaustion.stallHoursSinceLastWeakIc0,
    exhaustionThresholds: exhaustion.exhaustionThresholds,
  };
}

function metricsFromEntry(entry?: EntryObservabilityResponse) {
  const windows24h = entry?.noTradeCauseBreakdownWindows?.["24h"];
  const entryTempo24h = entry?.entryTempo?.windows?.["24h"];
  const enterVerdicts = nullishNumber(entryTempo24h?.enterVerdicts);
  return {
    entryCandidates24h: nullishNumber(entryTempo24h?.entryNotes),
    noTradeDecisions24h: nullishNumber(windows24h?.terminalNoTradeDecisions),
    contradictionNoTrade24h: nullishNumber(
      windows24h?.noTradeCauseBreakdown?.market_reaction_contradiction,
    ),
    enterVerdictsPerHour24h: enterVerdicts != null ? enterVerdicts / 24 : null,
  };
}

export function resolveReviewReadinessClient({
  cycle,
  entry,
  metrics = {},
}: {
  cycle?: CycleOperatorSummary | null;
  entry?: EntryObservabilityResponse;
  metrics?: {
    roundTripsSinceImprovement?: number | null;
    weakIc0RoundTripsSinceImprovement?: number | null;
    lastWeakIc0RoundTripAt?: string | null;
    roundTripsSinceLastWeakIc0?: number | null;
    entryCandidates24h?: number | null;
    noTradeDecisions24h?: number | null;
    contradictionNoTrade24h?: number | null;
    enterVerdictsPerHour24h?: number | null;
  };
} = {}): ReviewReadiness | null {
  if (!cycle || cycle.protocolVersion !== "profit-v1") return null;

  if (cycle.reviewReadiness) {
    const stored = cycle.reviewReadiness;
    const entryMetrics = metricsFromEntry(entry);
    const hasLiveRegimeMetrics =
      metrics.noTradeDecisions24h != null ||
      metrics.contradictionNoTrade24h != null ||
      entryMetrics.noTradeDecisions24h != null ||
      entryMetrics.contradictionNoTrade24h != null;
    const storedMetrics = hydrateMetricsFromQualityLayer(
      {
        ...entryMetrics,
        ...metrics,
        noTradeDecisions24h:
          metrics.noTradeDecisions24h ?? entryMetrics.noTradeDecisions24h ?? null,
        contradictionNoTrade24h:
          metrics.contradictionNoTrade24h ?? entryMetrics.contradictionNoTrade24h ?? null,
        enterVerdictsPerHour24h:
          metrics.enterVerdictsPerHour24h ?? entryMetrics.enterVerdictsPerHour24h ?? null,
      },
      stored.qualityLayer,
    );
    let result = resolveReviewReadinessFromParts(cycle, storedMetrics);
    const awaitingShip = result.status === "awaiting_ship" || stored.status === "awaiting_ship";
    if (awaitingShip) {
      result = {
        ...result,
        status: "awaiting_ship",
        bindingReady: false,
        diagnosticEligible: false,
        fillLayer: {
          ...result.fillLayer,
          target: null,
          remaining: null,
          percent: null,
          met: false,
        },
      };
    }
    if (!hasLiveRegimeMetrics && stored.regimeLayer) {
      const preserveStoredReadiness =
        stored.regimeLayer.contradictionRate24h != null || stored.status === "diagnostic";
      const preserved = {
        ...result,
        regimeLayer: stored.regimeLayer,
        bindingReady: awaitingShip ? false : result.bindingReady,
        diagnosticEligible: awaitingShip
          ? false
          : (stored.diagnosticEligible ?? result.diagnosticEligible),
        metricsSource: stored.metricsSource,
      };
      if (preserveStoredReadiness) {
        preserved.status = stored.status;
        preserved.statusLabelKo = stored.statusLabelKo;
        preserved.detailKo = stored.detailKo;
        preserved.metaKo = stored.metaKo;
      }
      if (result.bindingReady && stored.status !== "ready") {
        preserved.status = result.status;
        preserved.statusLabelKo = result.statusLabelKo;
        preserved.detailKo = result.detailKo;
        preserved.metaKo = result.metaKo;
      }
      return preserved;
    }
    return { ...result, metricsSource: stored.metricsSource };
  }

  const entryMetrics = metricsFromEntry(entry);
  let roundTripsSinceImprovement = metrics.roundTripsSinceImprovement ?? null;
  if (roundTripsSinceImprovement == null && cycle.fillsSinceLastImprovement != null) {
    roundTripsSinceImprovement = Math.floor(
      cycle.fillsSinceLastImprovement * ROUND_TRIP_FILL_PROXY_RATIO,
    );
  }

  return resolveReviewReadinessFromParts(cycle, {
    ...entryMetrics,
    ...metrics,
    roundTripsSinceImprovement,
  });
}

function resolveReviewReadinessFromParts(
  cycle: CycleOperatorSummary,
  metrics: {
    roundTripsSinceImprovement?: number | null;
    weakIc0RoundTripsSinceImprovement?: number | null;
    lastWeakIc0RoundTripAt?: string | null;
    roundTripsSinceLastWeakIc0?: number | null;
    entryCandidates24h?: number | null;
    noTradeDecisions24h?: number | null;
    contradictionNoTrade24h?: number | null;
    enterVerdictsPerHour24h?: number | null;
  },
): ReviewReadiness {
  const f = nullishNumber(cycle.fills);
  const fillsPerReview = cycle.fillsPerReview ?? DEFAULT_FILLS_PER_REVIEW;
  const minRt = DEFAULT_MIN_ROUND_TRIPS_PER_REVIEW;
  const fillTarget = resolvePositiveFillGate(cycle.nextReviewAtFills);
  const baseline =
    cycle.fills != null && cycle.fillsSinceLastImprovement != null
      ? cycle.fills - cycle.fillsSinceLastImprovement
      : null;
  const stallCapTarget = baseline != null ? baseline + fillsPerReview : null;

  const fillLayerMet =
    f != null &&
    ((fillTarget != null && f >= fillTarget) || (stallCapTarget != null && f >= stallCapTarget));

  let fillLayer: ReviewReadinessLayer = {
    ...buildLayer(f, fillTarget, fillLayerMet),
    stallCapTarget,
  };
  if (isAwaitingImprovementShip(cycle)) {
    fillLayer = { ...fillLayer, target: null, remaining: null, percent: null, met: false };
  }

  const totalRt = nullishNumber(metrics.roundTripsSinceImprovement);
  const qualityLayer = resolveQualityLayer({ metrics, minRt });

  const awaitingShip = isAwaitingImprovementShip(cycle);
  const bindingReady = !awaitingShip && fillLayerMet && qualityLayer.met;

  const noTrade24h = nullishNumber(metrics.noTradeDecisions24h) ?? 0;
  const contradiction24h = nullishNumber(metrics.contradictionNoTrade24h) ?? 0;
  const contradictionRate24h = noTrade24h > 0 ? contradiction24h / noTrade24h : null;
  const enterPerHour = nullishNumber(metrics.enterVerdictsPerHour24h);

  const regimeCrisis =
    contradictionRate24h != null &&
    contradictionRate24h >= REGIME_CRISIS_CONTRADICTION_SHARE &&
    enterPerHour != null &&
    enterPerHour <= ENTER_VERDICTS_PER_HOUR_NEAR_ZERO;

  const entryCandidates24h = nullishNumber(metrics.entryCandidates24h) ?? 0;
  const diagnosticEligible =
    !awaitingShip &&
    regimeCrisis &&
    (entryCandidates24h >= DIAGNOSTIC_MIN_ENTRY_CANDIDATES_24H ||
      (totalRt ?? 0) >= DIAGNOSTIC_MIN_ROUND_TRIPS);

  const regimeLayer = {
    contradictionRate24h,
    crisis: regimeCrisis,
    stable: contradictionRate24h != null ? !regimeCrisis : null,
  };

  const nearGate = fillLayer.remaining != null && fillLayer.remaining <= fillsPerReview;

  let status: ReviewReadinessStatus = "unknown";
  let statusLabelKo = "미설정";
  let detailKo = "리뷰 준비 상태 확인 불가";
  let metaKo: string | undefined;

  if (isAwaitingImprovementShip(cycle)) {
    status = "awaiting_ship";
    statusLabelKo = "배포 대기";
    detailKo =
      f != null
        ? `개선 배포 대기 · 프로토콜 ${f} fill`
        : "개선 배포 대기 — cycle:improvement 후 리뷰 게이트 설정";
  } else if (!fillTarget) {
    status = "unknown";
    statusLabelKo = "미설정";
    detailKo = "다음 리뷰 fill 미설정";
  } else if (bindingReady) {
    status = "ready";
    statusLabelKo = "판정";
    const qualityLabel = formatQualityLayerLabel(qualityLayer) ?? "RT ?/?";
    detailKo = `${qualityLabel} · fill ${fillLayer.actual}/${fillLayer.target} — 판정 가능`;
    if (qualityLayer.weakSliceExhausted) {
      detailKo += " — weak·ic0 정체, total RT 기준 판정";
    }
  } else if (diagnosticEligible) {
    status = "diagnostic";
    statusLabelKo = "진단";
    const contradictionPct =
      contradictionRate24h != null ? Math.round(contradictionRate24h * 100) : "?";
    const enterLabel = enterPerHour != null ? enterPerHour.toFixed(1) : "?";
    detailKo = `모순 ${contradictionPct}% · enter ${enterLabel}/h — 진단 메모만`;
    const qualityLabel = formatQualityLayerLabel(qualityLayer) ?? "RT ?/?";
    metaKo = `${qualityLabel} · fill ${fillLayer.actual}/${fillLayer.target}`;
  } else if (cycle.improvementPhase === "post-improvement" && (fillLayerMet || nearGate)) {
    status = "prep";
    statusLabelKo = "준비";
    const qualityLabel = formatQualityLayerLabel(qualityLayer) ?? "RT ?/?";
    detailKo = `${qualityLabel} · fill ${fillLayer.actual}/${fillLayer.target}`;
    if (!qualityLayer.met && fillLayer.remaining != null && fillLayer.remaining > 0) {
      metaKo = `품질층 미달 · fill ${fillLayer.remaining}건 남음`;
    } else if (!qualityLayer.met) {
      metaKo = "품질층 미달 — RT 표본 축적 중";
    }
  } else if (cycle.improvementPhase === "post-improvement") {
    status = "counting";
    statusLabelKo = "집계";
    detailKo =
      fillLayer.remaining != null
        ? `다음 리뷰까지 ${fillLayer.remaining}건 (@${fillTarget})`
        : `리뷰 게이트 @${fillTarget} 집계 중`;
    metaKo = formatQualityLayerLabel(qualityLayer) ?? "RT ?/?";
  } else {
    status = "unknown";
    statusLabelKo = "미설정";
    detailKo = "개선 후 baseline 미설정";
  }

  return {
    protocolLabel: "profit-v1.1",
    status,
    statusLabelKo,
    bindingReady,
    diagnosticEligible,
    fillLayer,
    qualityLayer,
    regimeLayer,
    detailKo,
    metaKo,
  };
}

export function reviewReadinessTone(
  status: ReviewReadinessStatus,
): "green" | "blue" | "amber" | "rose" {
  if (status === "ready") return "green";
  if (status === "diagnostic") return "rose";
  if (status === "prep") return "amber";
  if (status === "awaiting_ship") return "amber";
  return "blue";
}

export function formatReviewReadinessLayers(readiness: ReviewReadiness | null | undefined) {
  if (!readiness) return null;
  const fillTarget = readiness.fillLayer.target;
  const showFill =
    readiness.status !== "awaiting_ship" &&
    readiness.fillLayer.actual != null &&
    fillTarget != null &&
    fillTarget > 0;
  const fill = showFill ? `fill ${readiness.fillLayer.actual}/${fillTarget}` : null;
  const rt = formatQualityLayerLabel(readiness.qualityLayer);
  return [rt, fill].filter(Boolean).join(" · ");
}
